import {useEffect, useRef} from 'react';
import {useLocation, useNavigate, useNavigationType} from 'react-router';

import NAVIGATION_TYPES from '@/consts/navigationTypes';

/**
 * Registers an open popup in the iframe's browser history so that a native
 * back gesture (or browser back button) closes the popup instead of leaving
 * the page.
 *
 * Supports nested popups: only closes on a POP navigation (real back),
 * so an inner popup pushing its own param does not accidentally close outer ones.
 *
 * Usage:
 *   const {onClose} = usePopupRoute({popupKey: 'badge-detail', open, onClose: () => setOpen(false)});
 *   // use `onClose` wherever a close-button calls back.
 */
const usePopupRoute = ({popupKey, open, onClose}: {popupKey: string; open: boolean; onClose: () => void}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();

    const popupParam = new URLSearchParams(location.search).get('popup');
    const isParamActive = popupParam === popupKey;

    // Push a history entry when the popup opens (and the param isn't there yet).
    useEffect(() => {
        if (!open || isParamActive) return;

        const search = new URLSearchParams(location.search);
        search.set('popup', popupKey);
        navigate(`${location.pathname}?${search.toString()}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Detect browser back: param was active, then disappeared via a POP (real back).
    // Ignore PUSH (an inner popup opened and overwrote the param).
    const prevIsParamActiveRef = useRef(false);
    useEffect(() => {
        const wasActive = prevIsParamActiveRef.current;
        prevIsParamActiveRef.current = isParamActive;

        if (open && wasActive && !isParamActive && navigationType === NAVIGATION_TYPES.POP) {
            onClose();
        }
    }, [isParamActive, navigationType, open, onClose]);

    // When the popup closes programmatically (X button), pop our history entry.
    const handleClose = () => {
        onClose();
        if (isParamActive) {
            navigate(-1);
        }
    };

    return {onClose: handleClose};
};

export default usePopupRoute;
